import { AlulaResponseData } from "@app/shared/databases/alula.database";
import { ImportMap } from "@app/shared/resources/class-importer-exporter.service";

/**
 * Numeric to String mapping of Relay Status to allow user display.
 */
const relayStatusText = {
    0: "Idle",
    1: "Relay Attempted",
    2: "Relay Suppressed",
    3: "Relay Timed Out",
    4: "No Receivers Configured",
    8: "Destination Unreachable",
    9: "Relay Failed",
    10: "Relay Complete",
};

export const ALULA_RELAY_STATUSES = [
    {
        value: 0,
        label: "Not Relay",
    },
    {
        value: 1,
        label: "Attempting to Relay",
    },
    {
        value: 2,
        label: "Suppressed",
    },
    {
        value: 3,
        label: "Timed Out, Cannot Relay",
    },
    {
        value: 4,
        label: "No Receivers Configured, Cannot Relay",
    },
    {
        value: 8,
        label: "No Start Tone",
    },
    {
        value: 9,
        label: "Negative Acknowledgement",
    },
    {
        value: 10,
        label: "Relay Completed",
    },
];

export enum ALULA_DATE_FILTER_EVENT_KEY {
    GREATER_THEN = "$gte",
    LESS_THEN = "$lte",
}

export type AlulaEventDateFilter = {
    [key in ALULA_DATE_FILTER_EVENT_KEY]?: string;
};

export interface AlulaEventFilter {
    signalEventCode?: string;
    relayStatus?: number;
    signalUserZone?: string;
    dateEntered?: AlulaEventDateFilter[];
}

interface AlulaEventInt {
    id: string;
    deviceId: string;
    dealerId: string;
    customerId: string;
    groupId: string;
    dateEntered: string;
    relay: number;
    action: number;
    npv: number;
    relayStatus: string;
    signalAccountNumber: string;
    signalEventQualifier: string;
    signalEventCode: string;
    signalEventDescription: string;
    signalPartition: string;
    signalUserZone: string;
    signalUserZoneType: string;
    signalUserZoneAlias: string;
    signalProtocol: string;
}

interface AlulaEventData {
    id: string;
    deviceId: string;
    dealerId: string;
    customerId: string;
    groupId: string;
    dateEntered: string;
    relay: number;
    action: number;
    npv: number;
    relayStatus: number;
    signalAccountNumber: string;
    signalEventQualifier: string;
    signalEventCode: string;
    signalEventDescription: string;
    signalPartition: string;
    signalUserZone: string;
    signalUserZoneType: string;
    signalUserZoneAlias: string;
    signalProtocol: string;
}

export class AlulaEvent {
    formatedDateEntered: string;

    static readonly importMap: ImportMap<AlulaEventInt, AlulaEventData> = [
        { i: "id", o: "id" },
        { i: "deviceId", o: "deviceId" },
        { i: "dealerId", o: "dealerId" },
        { i: "customerId", o: "customerId" },
        { i: "groupId", o: "groupId" },
        { i: "dateEntered", o: "dateEntered" },
        { i: "relay", o: "relay" },
        { i: "action", o: "action" },
        { i: "npv", o: "npv" },
        { i: "relayStatus", o: "relayStatus" },
        { i: "signalAccountNumber", o: "signalAccountNumber" },
        { i: "signalEventQualifier", o: "signalEventQualifier" },
        { i: "signalEventCode", o: "signalEventCode" },
        { i: "signalEventDescription", o: "signalEventDescription" },
        { i: "signalPartition", o: "signalPartition" },
        { i: "signalUserZone", o: "signalUserZone" },
        { i: "signalUserZoneType", o: "signalUserZoneType" },
        { i: "signalUserZoneAlias", o: "signalUserZoneAlias" },
        { i: "signalProtocol", o: "signalProtocol" },
    ];

    constructor(
        public id: string,
        public deviceId: string,
        public dealerId: string,
        public customerId: string,
        public groupId: string,
        public dateEntered: string,
        public relay: number,
        public action: number,
        public npv: number,
        public relayStatus: string,
        public signalAccountNumber: string,
        public signalEventQualifier: string,
        public signalEventCode: string,
        public signalEventDescription: string,
        public signalPartition: string,
        public signalUserZone: string,
        public signalUserZoneType: string,
        public signalUserZoneAlias: string,
        public signalProtocol: string
    ) {}

    static fromDbObject(dbEvent: AlulaResponseData): AlulaEvent {
        return new AlulaEvent(
            dbEvent.id,
            dbEvent.attributes[AlulaEvent.importMap[1].i],
            dbEvent.attributes[AlulaEvent.importMap[2].i],
            dbEvent.attributes[AlulaEvent.importMap[3].i],
            dbEvent.attributes[AlulaEvent.importMap[4].i],
            dbEvent.attributes[AlulaEvent.importMap[5].i],
            dbEvent.attributes[AlulaEvent.importMap[6].i],
            dbEvent.attributes[AlulaEvent.importMap[7].i],
            dbEvent.attributes[AlulaEvent.importMap[8].i],
            this.getRelayStatus(dbEvent.attributes[AlulaEvent.importMap[9].i]),
            dbEvent.attributes[AlulaEvent.importMap[10].i],
            dbEvent.attributes[AlulaEvent.importMap[11].i],
            dbEvent.attributes[AlulaEvent.importMap[12].i],
            dbEvent.attributes[AlulaEvent.importMap[13].i],
            dbEvent.attributes[AlulaEvent.importMap[14].i],
            dbEvent.attributes[AlulaEvent.importMap[15].i],
            dbEvent.attributes[AlulaEvent.importMap[16].i],
            dbEvent.attributes[AlulaEvent.importMap[17].i],
            dbEvent.attributes[AlulaEvent.importMap[18].i]
        );
    }

    static getRelayStatus(status: number): string {
        return ALULA_RELAY_STATUSES.find(relayStatus => status === relayStatus.value)?.label || "";
    }

    exportEventProperties() {
        return {
            id: this.id,
            class: "alarmEvent",
            dateEntered: this.dateEntered,
            signalEventDescription: this.signalEventDescription,
            signalEventCode: this.signalEventCode,
            signalUserZone: this.signalUserZone,
            signalRelayStatus: AlulaEvent.getRelayStatus(this.relay),
        };
    }
}
